// ============================================================
// OILMED — Service Firestore
// ============================================================
// Toutes les opérations de lecture et d'écriture Firestore.
// Les composants React appellent ces fonctions directement.
//
// Modèle de données :
//   - terminaux/{t}/lots/{lotId}                            — sous douane
//   - terminaux/{t}/lots/{lotId}/dedouanements/{ddId}       — historique dédouanement
//   - terminaux/{t}/libre_pratique/{produit}                — LP stock
//   - commandes/{commandeId}                                — commandes (top-level)
//   - commandes/{commandeId}/livraisons/{livId}             — livraisons partielles
//   - stations/{stationId}                                  — stations
//   - journal/{id}                                          — log événements
//
// Logique métier :
//   - LP déduit à la livraison (via watchTerminalStocks qui soustrait livraisons)
//   - Livraisons partielles multiples sous une même commande
//   - Capacité station vérifiée avant création commande ET avant livraison
//   - Toute écriture significative produit une entrée dans /journal
// ============================================================

import { db } from "../../../config/firebaseConfig";
import {
  doc,
  collection,
  getDocs,
  getDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  Timestamp,
  query,
  where,
  orderBy,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  increment,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { fmtDec } from "./utils";

function journalId(reference) {
  const base = String(reference || Date.now())
    .replace(/\//g, "_")
    .replace(/\s+/g, "_");
  return `${base}_${Date.now()}`;
}

// Les livraisons orphelines ne sont comptabilisées dans le LP qu'à partir du
// 12/08/2026. Celles d'avant sont déjà intégrées au solde LP de base
// (ajustement manuel « Achat en libre pratique » du 11/08) — les soustraire
// reviendrait à les compter deux fois.
export const ORPHELINES_LP_DEPUIS = new Date(2026, 7, 12); // 12 août 2026

export function orphelineCompteDansLP(data) {
  const d = data?.createdAt?.toDate?.() ?? null;
  return d ? d >= ORPHELINES_LP_DEPUIS : false;
}

// ════════════════════════════════════════════════════════════
// RÉPARTITION DU LIBRE PRATIQUE PAR FOURNISSEUR
// libre_pratique/{produit}.repartition = {
//   depuis: Timestamp,              // début du suivi (solde d'ouverture)
//   fournisseurs: { NOM: m³ },      // ouverture + entrées attribuées depuis
// }
// Solde fournisseur = fournisseurs[F] − livraisons (datées ≥ depuis) des
// commandes de ce fournisseur et des livraisons sans commande qui lui sont
// attribuées (livraisons_orphelines.fournisseur). « Non attribué » = solde total − Σ fournisseurs
// (stock d'avant les soldes d'ouverture, livraisons sans commande, commandes
// ou entrées sans fournisseur). Sans soldes d'ouverture (pas de depuis), les
// entrées déjà attribuées comptent quand même : demarree = false.
// Même calcul que functions/lpBalance.js.
// ════════════════════════════════════════════════════════════

export const NON_ATTRIBUE = "Non attribué";

export function normFournisseur(nom) {
  return String(nom || "").trim().toUpperCase();
}

const toMillis = (v) =>
  v?.toMillis?.() ?? (v instanceof Date ? v.getTime() : null);

/**
 * @param {object|null} repartition - champ repartition du doc libre_pratique
 * @param {number} total - solde LP total (même formule que watchTerminalStocks)
 * @param {{ fournisseur?: string, volumeM3: number, date?: any }[]} livraisons
 *        livraisons des commandes du terminal/produit (date = date de livraison)
 * @returns {null | { depuis, demarree: boolean, parFournisseur: Object<string,number>, nonAttribue: number }}
 */
export function calculerRepartitionLP(repartition, total, livraisons) {
  if (!repartition) return null;
  const depuisMs = repartition.depuis ? toMillis(repartition.depuis) ?? 0 : 0;
  const parFournisseur = {};
  for (const [f, v] of Object.entries(repartition.fournisseurs || {})) {
    parFournisseur[f] = Number(v) || 0;
  }
  for (const l of livraisons) {
    const f = normFournisseur(l.fournisseur);
    if (!f) continue;
    // Date en attente d'écriture serveur (null) → livraison qui vient d'arriver
    const ms = toMillis(l.date) ?? Date.now();
    if (ms < depuisMs) continue;
    parFournisseur[f] = (parFournisseur[f] || 0) - (Number(l.volumeM3) || 0);
  }
  const somme = Object.values(parFournisseur).reduce((a, v) => a + v, 0);
  return {
    depuis: repartition.depuis || null,
    // Soldes d'ouverture saisis : le contrôle LP par fournisseur s'applique
    demarree: !!repartition.depuis,
    parFournisseur,
    nonAttribue: total - somme,
  };
}

export function isPastDateOnly(dateValue) {
  if (!dateValue) return false;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return false;

  // Compare date-only values in local time.
  const selected = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date();
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  return selected < todayOnly;
}

// Resolve { uid, name } of the currently authenticated user for journal entries.
async function getSubmittedBy() {
  const u = getAuth().currentUser;
  if (!u) return null;
  try {
    const snap = await getDoc(doc(db, "app_users", u.uid));
    const name = snap.exists()
      ? snap.data().name
      : u.displayName || u.email || u.uid;
    return { uid: u.uid, name };
  } catch {
    return { uid: u.uid, name: u.displayName || u.email || u.uid };
  }
}

// ════════════════════════════════════════════════════════════
// LECTURES — Temps réel (onSnapshot)
// ════════════════════════════════════════════════════════════

/**
 * Écoute les stocks d'un terminal en temps réel.
 * - Sous douane = somme des lots du terminal
 * - Libre pratique = LP doc - somme des livraisons des commandes du terminal
 *
 * @param {'SEP'|'LP'} terminalId
 * @param {Function} callback - Reçoit { essence: StockProduit, go: StockProduit }
 * @returns {Function} unsubscribe
 */
export function watchTerminalStocks(terminalId, callback) {
  // Watch both lots_su (essence) and lots_go (mazout) collections
  const lotsSuRef = collection(db, "terminaux", terminalId, "lots_su");
  const lotsGoRef = collection(db, "terminaux", terminalId, "lots_go");
  const lpRef = collection(db, "terminaux", terminalId, "libre_pratique");
  const commandesRef = query(
    collection(db, "commandes"),
    where("terminal", "==", terminalId),
  );

  let latestLots = {
    essence: { sd: 0, sd_init: 0 },
    go: { sd: 0, sd_init: 0 },
  };
  let latestLP = { essence: 0, go: 0 };
  let latestRepartition = { essence: null, go: null };
  let latestDeliveries = { essence: 0, go: 0 };
  let orphanDeliveries = { essence: 0, go: 0 };
  // Livraisons sans commande attribuées à un fournisseur (orpheline.fournisseur)
  let orphanFournisseurs = { essence: [], go: [] };
  const cmdInfo = new Map(); // commandeId → { produit, fournisseur }
  const livDetails = new Map(); // commandeId → [{ volumeM3, date }]

  const emitUpdate = () => {
    const livraisonsPar = { essence: [], go: [] };
    for (const [cmdId, livs] of livDetails) {
      const info = cmdInfo.get(cmdId);
      if (!info || !livraisonsPar[info.produit]) continue;
      for (const l of livs) {
        livraisonsPar[info.produit].push({ ...l, fournisseur: info.fournisseur });
      }
    }
    for (const p of ["essence", "go"]) livraisonsPar[p].push(...orphanFournisseurs[p]);
    const out = {};
    for (const p of ["essence", "go"]) {
      // Pas de plancher à 0 : une livraison faite avant la saisie du LP
      // rend le solde négatif, corrigé quand le LP est ajouté.
      const lp = latestLP[p] - latestDeliveries[p] - orphanDeliveries[p];
      out[p] = {
        sd: latestLots[p].sd,
        lp,
        sd_init: latestLots[p].sd_init,
        repartition: calculerRepartitionLP(latestRepartition[p], lp, livraisonsPar[p]),
      };
    }
    callback(out);
  };

  const onSnapErr = (label) => (err) => {
    console.warn(`[watchTerminalStocks] ${label} permission error:`, err.code);
  };

  // Watch lots_su (essence)
  const unsubLotsSu = onSnapshot(
    lotsSuRef,
    (lotsSnap) => {
      latestLots.essence = { sd: 0, sd_init: 0 };
      lotsSnap.forEach((d) => {
        const lot = d.data();
        latestLots.essence.sd += lot.volumeM3 || 0;
        latestLots.essence.sd_init += lot.volumeM3_initial || 0;
      });
      emitUpdate();
    },
    onSnapErr("lots_su"),
  );

  // Watch lots_go (mazout)
  const unsubLotsGo = onSnapshot(
    lotsGoRef,
    (lotsSnap) => {
      latestLots.go = { sd: 0, sd_init: 0 };
      lotsSnap.forEach((d) => {
        const lot = d.data();
        latestLots.go.sd += lot.volumeM3 || 0;
        latestLots.go.sd_init += lot.volumeM3_initial || 0;
      });
      emitUpdate();
    },
    onSnapErr("lots_go"),
  );

  const unsubLP = onSnapshot(
    lpRef,
    (lpSnap) => {
      latestLP = { essence: 0, go: 0 };
      latestRepartition = { essence: null, go: null };
      lpSnap.forEach((d) => {
        const produit = d.id;
        if (latestLP[produit] !== undefined) {
          latestLP[produit] = d.data().volumeM3 || 0;
          latestRepartition[produit] = d.data().repartition || null;
        }
      });
      emitUpdate();
    },
    onSnapErr("libre_pratique"),
  );

  // Watch commandes for this terminal → sum livraisons subcollection
  const livUnsubs = new Map(); // commandeId → livraisons unsub
  const livSums = new Map(); // commandeId → { produit, total }

  const unsubCommandes = onSnapshot(
    commandesRef,
    (cmdSnap) => {
      // Detach livraisons listeners for removed commandes
      const currentIds = new Set(cmdSnap.docs.map((d) => d.id));
      for (const [id, unsub] of livUnsubs) {
        if (!currentIds.has(id)) {
          unsub();
          livUnsubs.delete(id);
          livSums.delete(id);
          livDetails.delete(id);
          cmdInfo.delete(id);
        }
      }
      // Fournisseur à jour (modifiable après création)
      cmdSnap.docs.forEach((d) => {
        const c = d.data();
        cmdInfo.set(d.id, { produit: c.produit, fournisseur: c.fournisseur || null });
      });

      // Attach listeners for new commandes
      cmdSnap.docs.forEach((cmdDoc) => {
        if (livUnsubs.has(cmdDoc.id)) return;
        const cmd = cmdDoc.data();
        const livRef = collection(db, "commandes", cmdDoc.id, "livraisons");
        const livUnsub = onSnapshot(
          livRef,
          (livSnap) => {
            const total = livSnap.docs.reduce(
              (sum, d) => sum + (d.data().volumeM3 || 0),
              0,
            );
            livSums.set(cmdDoc.id, { produit: cmd.produit, total });
            livDetails.set(
              cmdDoc.id,
              livSnap.docs.map((d) => ({
                volumeM3: d.data().volumeM3 || 0,
                date: d.data().date || d.data().createdAt || null,
              })),
            );
            // Recompute deliveries
            latestDeliveries = { essence: 0, go: 0 };
            for (const { produit, total } of livSums.values()) {
              if (produit && latestDeliveries[produit] !== undefined) {
                latestDeliveries[produit] += total;
              }
            }
            emitUpdate();
          },
          onSnapErr(`livraisons/${cmdDoc.id}`),
        );
        livUnsubs.set(cmdDoc.id, livUnsub);
      });
      emitUpdate();
    },
    onSnapErr("commandes"),
  );

  // Watch livraisons_orphelines — livraisons sans commande correspondante
  const orphelinesRef = collection(
    db,
    "terminaux",
    terminalId,
    "livraisons_orphelines",
  );
  const unsubOrphelines = onSnapshot(
    orphelinesRef,
    (snap) => {
      orphanDeliveries = { essence: 0, go: 0 };
      orphanFournisseurs = { essence: [], go: [] };
      snap.forEach((d) => {
        const data = d.data();
        if (!orphelineCompteDansLP(data)) return; // avant le 12/08/2026 → déjà dans le solde de base
        const { produit, volumeM3 } = data;
        if (produit && orphanDeliveries[produit] !== undefined) {
          orphanDeliveries[produit] += volumeM3 || 0;
          if (data.fournisseur) {
            orphanFournisseurs[produit].push({
              fournisseur: data.fournisseur,
              volumeM3: volumeM3 || 0,
              date: data.createdAt || null,
            });
          }
        }
      });
      emitUpdate();
    },
    onSnapErr("livraisons_orphelines"),
  );

  return () => {
    unsubLotsSu();
    unsubLotsGo();
    unsubLP();
    unsubCommandes();
    for (const unsub of livUnsubs.values()) unsub();
    unsubOrphelines();
  };
}

/**
 * Écoute toutes les stations en temps réel.
 */
export function watchStations(callback) {
  const q = query(collection(db, "stations"), orderBy("ordre"));
  return onSnapshot(
    q,
    (snap) => {
      const stations = {};
      snap.forEach((d) => {
        stations[d.id] = { id: d.id, ...d.data() };
      });
      callback(stations);
    },
    (err) => console.warn("[watchStations] permission error:", err.code),
  );
}

/**
 * Écoute toutes les commandes (top-level) en temps réel.
 * Pour chaque commande, charge sa sous-collection livraisons.
 *
 * @param {Function} callback - Reçoit Commande[] avec { livraisons, totalLivre, statut }
 * @param {{start:Date,end:Date}|null} [range] - Limite aux commandes créées dans ce mois
 * @returns {Function} unsubscribe
 */
export function watchCommandes(callback, range = null) {
  const constraints = [orderBy("createdAt", "desc")];
  if (range) {
    constraints.push(where("createdAt", ">=", Timestamp.fromDate(range.start)));
    constraints.push(where("createdAt", "<", Timestamp.fromDate(range.end)));
  }
  const q = query(collection(db, "commandes"), ...constraints);
  let cmdDocs = [];
  const livMap = new Map(); // commandeId → livraisons[]
  const livUnsubs = new Map();

  const emit = () => {
    const out = cmdDocs.map((d) => {
      const cmd = { id: d.id, ...d.data() };
      const livraisons = livMap.get(d.id) || [];
      const totalLivre = livraisons.reduce((s, l) => s + (l.volumeM3 || 0), 0);
      const statut =
        totalLivre >= (cmd.qtyCom || 0)
          ? "cloture"
          : totalLivre > 0
            ? "partiel"
            : "en_attente";
      return { ...cmd, livraisons, totalLivre, statut };
    });
    callback(out);
  };

  const unsubMain = onSnapshot(
    q,
    (snap) => {
      cmdDocs = snap.docs;
      const currentIds = new Set(snap.docs.map((d) => d.id));

      // Detach removed listeners
      for (const [id, unsub] of livUnsubs) {
        if (!currentIds.has(id)) {
          unsub();
          livUnsubs.delete(id);
          livMap.delete(id);
        }
      }

      // Attach new livraisons listeners
      snap.docs.forEach((cmdDoc) => {
        if (livUnsubs.has(cmdDoc.id)) return;
        const livRef = collection(db, "commandes", cmdDoc.id, "livraisons");
        const livUnsub = onSnapshot(
          livRef,
          (livSnap) => {
            livMap.set(
              cmdDoc.id,
              livSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
            );
            emit();
          },
          (err) =>
            console.warn(
              `[watchCommandes] livraisons/${cmdDoc.id} error:`,
              err.code,
            ),
        );
        livUnsubs.set(cmdDoc.id, livUnsub);
      });

      emit();
    },
    (err) => console.warn("[watchCommandes] permission error:", err.code),
  );

  return () => {
    unsubMain();
    for (const unsub of livUnsubs.values()) unsub();
  };
}

/**
 * Écoute les livraisons orphelines (sans commande correspondante) d'un terminal.
 * Créées par la Cloud Function approvToJournal quand aucune commande ne matche le BL.
 *
 * @param {'SEP'|'LP'} terminalId
 * @param {Function} callback - Reçoit LivraisonOrpheline[] triées par date desc
 * @returns {Function} unsubscribe
 */
export function watchLivraisonsOrphelines(terminalId, callback) {
  const q = query(
    collection(db, "terminaux", terminalId, "livraisons_orphelines"),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(
    q,
    (snap) => {
      const orphelines = snap.docs.map((d) => ({
        id: d.id,
        terminal: terminalId,
        ...d.data(),
      }));
      callback(orphelines);
    },
    (err) =>
      console.warn("[watchLivraisonsOrphelines] permission error:", err.code),
  );
}

/**
 * Écoute le journal des mouvements en temps réel.
 * @param {{start:Date,end:Date}|null} [range] - Limite aux entrées créées dans ce mois
 */
export function watchJournal(callback, limite = 100, range = null) {
  const constraints = [orderBy("createdAt", "desc")];
  if (range) {
    constraints.push(where("createdAt", ">=", Timestamp.fromDate(range.start)));
    constraints.push(where("createdAt", "<", Timestamp.fromDate(range.end)));
  }
  const q = query(collection(db, "journal"), ...constraints);
  return onSnapshot(
    q,
    (snap) => {
      const entrees = snap.docs
        .slice(0, limite)
        .map((d) => ({ id: d.id, ...d.data() }));
      callback(entrees);
    },
    (err) => console.warn("[watchJournal] permission error:", err.code),
  );
}

// ════════════════════════════════════════════════════════════
// LECTURES PONCTUELLES
// ════════════════════════════════════════════════════════════

export async function getStockLP(terminalId, produit) {
  const ref = doc(db, "terminaux", terminalId, "libre_pratique", produit);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : { volumeM3: 0 };
}

/**
 * Solde du libre pratique : LP brut − livraisons des commandes − livraisons
 * orphelines (depuis le 12/08/2026). Jamais ramené à 0. Même formule que
 * watchTerminalStocks et functions/lpBalance.js.
 * @returns {{ solde: number, orphelines: object[], repartition: object|null }}
 *   orphelines comptées ; repartition = cf. calculerRepartitionLP
 */
export async function calculerSoldeLP(terminalId, produit) {
  const [lpSnap, cmdSnap, orphSnap] = await Promise.all([
    getDoc(doc(db, "terminaux", terminalId, "libre_pratique", produit)),
    getDocs(
      query(
        collection(db, "commandes"),
        where("terminal", "==", terminalId),
        where("produit", "==", produit),
      ),
    ),
    getDocs(
      query(
        collection(db, "terminaux", terminalId, "livraisons_orphelines"),
        where("produit", "==", produit),
      ),
    ),
  ]);
  const base = lpSnap.exists() ? lpSnap.data().volumeM3 || 0 : 0;
  let totalLivraisons = 0;
  const livraisons = [];
  await Promise.all(
    cmdSnap.docs.map(async (cmdDoc) => {
      const livSnap = await getDocs(
        collection(db, "commandes", cmdDoc.id, "livraisons"),
      );
      livSnap.forEach((l) => {
        const v = l.data().volumeM3 || 0;
        totalLivraisons += v;
        livraisons.push({
          fournisseur: cmdDoc.data().fournisseur || null,
          volumeM3: v,
          date: l.data().date || l.data().createdAt || null,
        });
      });
    }),
  );
  const orphelines = orphSnap.docs
    .map((d) => d.data())
    .filter(orphelineCompteDansLP);
  const totalOrphelines = orphelines.reduce((a, o) => a + (o.volumeM3 || 0), 0);
  const solde = base - totalLivraisons - totalOrphelines;
  // Une livraison sans commande peut être attribuée à un fournisseur
  for (const o of orphelines) {
    if (o.fournisseur) {
      livraisons.push({ fournisseur: o.fournisseur, volumeM3: o.volumeM3 || 0, date: o.createdAt || null });
    }
  }
  const repartition = calculerRepartitionLP(
    lpSnap.exists() ? lpSnap.data().repartition : null,
    solde,
    livraisons,
  );
  return { solde, orphelines, repartition };
}

/**
 * Quantité de LP qu'une nouvelle commande va réellement consommer.
 * Une ligne dont la livraison est déjà arrivée sans commande (livraison
 * orpheline, même N° BL et même station) a déjà été déduite du LP : seule
 * la part non encore livrée compte.
 * @param {{ stationId: string, qty: number, bl: string }[]} rows
 * @param {object[]} orphelines - livraisons orphelines du terminal/produit
 */
export function besoinLPCommande(rows, orphelines) {
  return rows.reduce((total, r) => {
    const bl = String(r.bl || "").trim();
    const dejaDeduit = orphelines
      .filter(
        (o) =>
          bl &&
          String(o.numBL || "").trim() === bl &&
          String(o.station || "").toLowerCase() === r.stationId.toLowerCase() &&
          orphelineCompteDansLP(o),
      )
      .reduce((a, o) => a + (o.volumeM3 || 0), 0);
    return total + Math.max(0, (r.qty || 0) - dejaDeduit);
  }, 0);
}

/**
 * Applique un ajustement au stock libre pratique.
 * - Incrémente terminaux/{terminal}/libre_pratique/{produit}.volumeM3
 * - Journalise dans terminaux/{terminal}/ajustements_lp/
 * @param {"ajustement"|"achat"} kind - "achat" = achat en libre pratique, sinon écart/correction
 */
export async function ajusterLP({
  terminalId,
  produit,
  deltaM3,
  note = "",
  date,
  userName = "",
  kind = "ajustement",
  fournisseur = null,
}) {
  if (!deltaM3 || deltaM3 === 0) throw new Error("Valeur d'ajustement requise");
  const f = normFournisseur(fournisseur);
  const lpRef = doc(db, "terminaux", terminalId, "libre_pratique", produit);

  // Solde réel avant ajustement (même formule que watchTerminalStocks),
  // négatif si des livraisons ont précédé la saisie du LP.
  const { solde: lpBefore } = await calculerSoldeLP(terminalId, produit);
  const lpAfter = lpBefore + deltaM3;

  await setDoc(
    lpRef,
    {
      volumeM3: increment(deltaM3),
      // Entrée attribuée au fournisseur (sinon → « Non attribué »)
      ...(f ? { repartition: { fournisseurs: { [f]: increment(deltaM3) } } } : {}),
    },
    { merge: true },
  );
  const logRef = collection(db, "terminaux", terminalId, "ajustements_lp");
  await addDoc(logRef, {
    produit,
    deltaM3,
    lpBefore,
    lpAfter,
    kind: kind === "achat" ? "achat" : "ajustement",
    fournisseur: f || null,
    userName: userName.trim(),
    note: note.trim(),
    date: date || new Date().toISOString().slice(0, 10),
    createdAt: serverTimestamp(),
  });
}

// ════════════════════════════════════════════════════════════
// FRAIS MISE EN PLACE — montant payé (CDF) pour la livraison d'un programme
// ════════════════════════════════════════════════════════════

/** Clé du document : un programme est propre à un terminal et un produit. */
export function fraisMiseEnPlaceId(terminalId, produit, numProg) {
  return `${terminalId}_${produit}_${String(numProg).replace(/\//g, "_")}`;
}

/** Écoute les frais mise en place d'un terminal+produit, indexés par numProg. */
export function watchFraisMiseEnPlace(terminalId, produit, callback) {
  const q = query(
    collection(db, "frais_mise_en_place"),
    where("terminal", "==", terminalId),
    where("produit", "==", produit),
  );
  return onSnapshot(
    q,
    (snap) => {
      const parProg = {};
      snap.forEach((d) => {
        const f = d.data();
        parProg[f.numProg] = { id: d.id, ...f };
      });
      callback(parProg);
    },
    (err) => console.warn("[watchFraisMiseEnPlace]", err.code),
  );
}

/**
 * Déclare (ou modifie) les frais mise en place d'un programme.
 * @param {{ terminalId, produit, numProg, montantCDF: number, date: string, note?: string }} p
 */
export async function enregistrerFraisMiseEnPlace({
  terminalId,
  produit,
  numProg,
  montantCDF,
  date,
  note = "",
}) {
  if (!numProg) throw new Error("Numéro de programme requis");
  if (!(montantCDF > 0)) throw new Error("Montant invalide");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) throw new Error("Date invalide");
  const submittedBy = await getSubmittedBy();
  const ref = doc(db, "frais_mise_en_place", fraisMiseEnPlaceId(terminalId, produit, numProg));
  const existing = await getDoc(ref);
  await setDoc(
    ref,
    {
      terminal: terminalId,
      produit,
      numProg: String(numProg),
      montantCDF,
      date,
      note: note.trim(),
      submittedBy,
      updatedAt: serverTimestamp(),
      ...(existing.exists() ? {} : { createdAt: serverTimestamp() }),
    },
    { merge: true },
  );
}

// ════════════════════════════════════════════════════════════
// PARAMÈTRES — taux des frais mise en place (CDF/m³) par terminal
// Stockés dans config/gestock.fraisMiseEnPlaceParM3 = { SEP, LP }
// (écriture réservée aux admins par les règles Firestore)
// ════════════════════════════════════════════════════════════

/** Écoute les taux de frais mise en place : callback({ SEP, LP }). */
export function watchTauxFraisMiseEnPlace(callback) {
  return onSnapshot(
    doc(db, "config", "gestock"),
    (snap) => callback(snap.data()?.fraisMiseEnPlaceParM3 || {}),
    (err) => console.warn("[watchTauxFraisMiseEnPlace]", err.code),
  );
}

/**
 * Enregistre les taux (CDF/m³) de frais mise en place. Un taux null ou
 * absent n'est pas modifié (fusion avec les taux existants).
 */
export async function enregistrerTauxFraisMiseEnPlace({ SEP, LP }) {
  const taux = {};
  for (const [terminal, v] of Object.entries({ SEP, LP })) {
    if (v === null || v === undefined) continue;
    if (!(v >= 0)) throw new Error(`Taux ${terminal} invalide`);
    taux[terminal] = v;
  }
  if (Object.keys(taux).length === 0) throw new Error("Aucun taux saisi");
  const submittedBy = await getSubmittedBy();
  await setDoc(
    doc(db, "config", "gestock"),
    {
      fraisMiseEnPlaceParM3: taux,
      fraisMiseEnPlaceMaj: { at: serverTimestamp(), by: submittedBy },
    },
    { merge: true },
  );
}

/** Écoute la liste des fournisseurs : callback({ SEP: [...], LP: [...] }). */
export function watchFournisseurs(callback) {
  return onSnapshot(
    doc(db, "config", "gestock"),
    (snap) => callback(snap.data()?.fournisseurs || {}),
    (err) => console.warn("[watchFournisseurs]", err.code),
  );
}

/** Remplace la liste des fournisseurs d'un terminal (admins). */
export async function enregistrerFournisseurs(terminalId, noms) {
  const liste = [...new Set((noms || []).map(normFournisseur).filter(Boolean))];
  const submittedBy = await getSubmittedBy();
  await setDoc(
    doc(db, "config", "gestock"),
    {
      fournisseurs: { [terminalId]: liste },
      fournisseursMaj: { at: serverTimestamp(), by: submittedBy },
    },
    { merge: true },
  );
}

/**
 * Démarre (ou redémarre) la répartition du LP par fournisseur : le solde
 * d'ouverture de chaque fournisseur doit totaliser le solde LP actuel.
 * @param {Object<string, number>} ouverture - { FOURNISSEUR: m³ }
 */
export async function demarrerRepartitionLP(terminalId, produit, ouverture) {
  const fournisseurs = {};
  for (const [nom, v] of Object.entries(ouverture || {})) {
    const f = normFournisseur(nom);
    const n = Number(v);
    if (!f || !Number.isFinite(n)) continue;
    fournisseurs[f] = (fournisseurs[f] || 0) + n;
  }
  if (Object.keys(fournisseurs).length === 0) {
    throw new Error("Saisissez le solde d'au moins un fournisseur");
  }
  const { solde } = await calculerSoldeLP(terminalId, produit);
  const somme = Object.values(fournisseurs).reduce((a, v) => a + v, 0);
  if (Math.abs(somme - solde) > 0.001) {
    throw new Error(
      `Les soldes (${fmtDec(somme, 3)} m³) doivent totaliser le libre pratique actuel ` +
        `(${fmtDec(solde, 3)} m³)`,
    );
  }
  const submittedBy = await getSubmittedBy();
  // updateDoc remplace tout le champ repartition (pas de fusion des anciens fournisseurs)
  await updateDoc(doc(db, "terminaux", terminalId, "libre_pratique", produit), {
    repartition: {
      depuis: serverTimestamp(),
      fournisseurs,
      ouverture: fournisseurs,
      soldeOuverture: solde,
      par: submittedBy,
    },
  });
}

/** Écoute l'historique des ajustements LP pour un terminal+produit. */
export function watchAjustementsLP(terminalId, produit, callback) {
  const q = query(
    collection(db, "terminaux", terminalId, "ajustements_lp"),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => d.produit === produit);
      callback(docs);
    },
    (err) => console.warn("[watchAjustementsLP]", err.code, err.message),
  );
}

export async function getStation(stationId) {
  const ref = doc(db, "stations", stationId);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ════════════════════════════════════════════════════════════
// ÉCRITURE — ENTRÉE DOUANE (création de lot)
// ════════════════════════════════════════════════════════════

/**
 * Crée un lot sous douane et écrit une entrée journal "Entrée Douane".
 *
 * @param {Object} params
 * @param {string} params.terminalId       - 'SEP' | 'LP'
 * @param {string} params.produit          - 'essence' | 'go'
 * @param {string} params.numeroPOT        - identifiant lot (ID Firestore)
 * @param {string} params.fournisseur
 * @param {number} params.volumeM3_initial
 * @param {number} params.tonnageTM_initial
 * @param {number} params.K
 * @param {string} params.date             - ISO date string
 * @returns {string} numeroPOT
 */
export async function creerEntreeDouane({
  terminalId,
  produit,
  numeroPOT,
  fournisseur,
  volumeM3_initial,
  tonnageTM_initial,
  K,
  date,
}) {
  // Determine collection name based on product
  // su = essence (super), go = mazout (gazole)
  const lotsCollection = produit === "essence" ? "lots_su" : "lots_go";

  // Sanitize numeroPOT for use as Firestore document ID
  // Replace slashes with underscores to avoid invalid document reference
  const docId = numeroPOT.replace(/\//g, "_");
  const lotRef = doc(db, "terminaux", terminalId, lotsCollection, docId);
  const submittedBy = await getSubmittedBy();
  const eventDate = date
    ? Timestamp.fromDate(new Date(date))
    : serverTimestamp();

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(lotRef);
    if (existing.exists()) {
      throw new Error(`Lot ${numeroPOT} existe déjà`);
    }

    tx.set(lotRef, {
      numeroPOT,
      fournisseur,
      volumeM3_initial,
      tonnageTM_initial,
      volumeM3: volumeM3_initial,
      tonnageTM: tonnageTM_initial,
      K,
      produit,
      terminal: terminalId,
      date: eventDate,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const journalRef = doc(db, "journal", journalId(numeroPOT));
    tx.set(journalRef, {
      type: "Entrée Douane",
      terminal: terminalId,
      produit,
      quantite: volumeM3_initial,
      tonnageTM: tonnageTM_initial,
      K,
      reference: numeroPOT,
      fournisseur,
      station: null,
      camion: null,
      submittedBy,
      note: `Entrée douane ${terminalId} ${produit} — POT ${numeroPOT} (${fournisseur})`,
      createdAt: serverTimestamp(),
      date: eventDate,
    });
  });

  return numeroPOT;
}

// ════════════════════════════════════════════════════════════
// ÉCRITURE — DÉDOUANEMENT PARTIEL
// ════════════════════════════════════════════════════════════

/**
 * Dédouane une quantité partielle :
 * - Diminue le stock sous douane (lot ou FIFO sur tous les lots)
 * - Augmente le stock libre pratique du terminal
 * - Crée une entrée /dedouanements sous le lot
 * - Ajoute une entrée dans le journal
 */
export async function dedouaner(
  terminalId,
  produit,
  quantite,
  reference,
  lotId = null,
  date = null,
) {
  const EPSILON = 1e-6;
  const eventDate = date ? new Date(date) : null;
  const lotsCollection = produit === "essence" ? "lots_su" : "lots_go";
  const lotsRef = collection(db, "terminaux", terminalId, lotsCollection);
  const lpRef = doc(db, "terminaux", terminalId, "libre_pratique", produit);

  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const ddCountByLot = {};
  if (lotId) {
    const ddSnap = await getDocs(
      collection(
        db,
        "terminaux",
        terminalId,
        lotsCollection,
        lotId,
        "dedouanements",
      ),
    );
    ddCountByLot[lotId] = ddSnap.size;
  }
  const submittedBy = await getSubmittedBy();

  await runTransaction(db, async (tx) => {
    const lpSnap = await tx.get(lpRef);

    let lotsToProcess = [];
    if (lotId) {
      const lotDoc = await tx.get(
        doc(db, "terminaux", terminalId, lotsCollection, lotId),
      );
      if (!lotDoc.exists()) throw new Error("Lot non trouvé");
      const lot = { id: lotDoc.id, ...lotDoc.data() };
      lotsToProcess = [lot];
    } else {
      const lotsSnap = await tx.get(lotsRef);
      lotsSnap.forEach((d) => {
        lotsToProcess.push({ id: d.id, ...d.data() });
      });
    }

    const totalSD = lotsToProcess.reduce(
      (sum, lot) => sum + (lot.volumeM3 || 0),
      0,
    );
    if (totalSD <= EPSILON) throw new Error("Aucun stock sous douane");
    if (quantite <= EPSILON) throw new Error("Quantité invalide");
    if (quantite - totalSD > EPSILON)
      throw new Error(
        `Stock sous douane insuffisant: ${fmtDec(totalSD, 3)} m³ disponibles`,
      );

    let remaining = quantite;
    const affectedLots = [];
    let totalTonnage = 0;
    let kCoefficient = 0;

    for (const lot of lotsToProcess) {
      if (remaining <= EPSILON) break;
      const lotRef = doc(db, "terminaux", terminalId, lotsCollection, lot.id);
      const available = lot.volumeM3 || 0;
      if (available <= EPSILON) continue;
      const toDeduct = Math.min(remaining, available);

      tx.update(lotRef, {
        volumeM3: Math.max(0, available - toDeduct),
        updatedAt: serverTimestamp(),
      });

      const lotK = lot.K || 1.33;
      const lotTonnage = toDeduct * lotK;
      totalTonnage += lotTonnage;
      kCoefficient = lotK;

      const numeroPOT = lot.numeroPOT || lot.bl || lot.pot || lot.id;
      affectedLots.push({
        lotId: lot.id,
        numeroPOT,
        fournisseur: normFournisseur(lot.fournisseur) || null,
        volumeDedouane: toDeduct,
        tonnageTM: lotTonnage,
      });

      const seq = (ddCountByLot[lot.id] ?? 0) + 1;
      ddCountByLot[lot.id] = seq;
      const ddId = `ded_${dateStr}_${seq}`;
      const ddRef = doc(
        db,
        "terminaux",
        terminalId,
        lotsCollection,
        lot.id,
        "dedouanements",
        ddId,
      );
      tx.set(ddRef, {
        volumeM3: toDeduct,
        tonnageTM: lotTonnage,
        reference,
        createdAt: serverTimestamp(),
        date: eventDate || serverTimestamp(),
      });

      remaining = Math.max(0, remaining - toDeduct);
    }

    if (remaining > EPSILON) {
      throw new Error(
        `Stock sous douane insuffisant: ${fmtDec(totalSD, 3)} m³ disponibles`,
      );
    }

    const currentLP = lpSnap.exists() ? lpSnap.data().volumeM3 || 0 : 0;
    // Entrée LP attribuée au fournisseur de chaque lot dédouané
    const parFournisseur = {};
    for (const l of affectedLots) {
      if (!l.fournisseur) continue;
      parFournisseur[l.fournisseur] = (parFournisseur[l.fournisseur] || 0) + l.volumeDedouane;
    }
    const fournisseursInc = Object.fromEntries(
      Object.entries(parFournisseur).map(([f, v]) => [f, increment(v)]),
    );
    tx.set(
      lpRef,
      {
        produit,
        volumeM3: currentLP + quantite,
        updatedAt: serverTimestamp(),
        ...(Object.keys(fournisseursInc).length
          ? { repartition: { fournisseurs: fournisseursInc } }
          : {}),
      },
      { merge: true },
    );

    const journalRef = doc(db, "journal", journalId(reference));
    tx.set(journalRef, {
      type: "Dédouanement",
      terminal: terminalId,
      produit,
      quantite,
      tonnageTM: totalTonnage,
      K: kCoefficient,
      reference,
      station: null,
      camion: null,
      lots: affectedLots,
      submittedBy,
      note: `Dédouanement ${terminalId} ${produit} → Libre pratique ${terminalId}`,
      createdAt: serverTimestamp(),
      date: eventDate || serverTimestamp(),
    });
  });
}

// ════════════════════════════════════════════════════════════
// ÉCRITURE — CRÉER UNE COMMANDE
// ════════════════════════════════════════════════════════════

/**
 * Crée une ou plusieurs commandes (un doc par ligne BL).
 *
 * IDs : `${STATION}_{NUMPROG}_{NUMBL}`.
 * LP n'est pas déduit ici — il l'est implicitement par les livraisons
 * (cf. watchTerminalStocks qui soustrait les livraisons du LP).
 *
 * @param {Object} params
 * @param {string} params.terminalId
 * @param {string} params.produit
 * @param {string} params.numProgramme
 * @param {Array<{stationId:string, qty:number, bl:string}>} params.rows
 * @param {string} [params.date]
 * @param {string} [params.fournisseur] - fournisseur dont le LP est consommé
 * @returns {string[]} IDs des commandes créées
 */
export async function creerCommande({
  terminalId,
  produit,
  numProgramme,
  rows,
  date,
  fournisseur = null,
}) {
  const fourn = normFournisseur(fournisseur) || null;
  const lpRef = doc(db, "terminaux", terminalId, "libre_pratique", produit);
  const isHistoricalCommande = isPastDateOnly(date);

  const activeRows = (rows || []).filter((r) => r.qty > 0 && r.bl?.trim());
  if (activeRows.length === 0) {
    throw new Error("Aucune ligne avec quantité > 0 et N° BL renseigné");
  }
  if (!numProgramme || !numProgramme.trim()) {
    throw new Error("Numéro de programme requis");
  }

  // ── Un N° BL est unique par terminal + produit ──
  const bls = activeRows.map((r) => r.bl.trim());
  if (bls.length > new Set(bls).size) {
    throw new Error("Le même N° BL est saisi plusieurs fois");
  }
  const blsExistants = await blsDejaUtilises(terminalId, produit, bls);
  if (blsExistants.length > 0) {
    throw new Error(
      `N° BL déjà utilisé pour ${terminalId} ${produit} : ` +
        blsExistants.map((c) => `${c.numBL} (${c.id})`).join(", "),
    );
  }

  // ── Libre pratique suffisant ? ──
  // Une commande ne peut pas dépasser le LP disponible. Exceptions : les
  // commandes datées dans le passé (régularisation de livraisons déjà
  // faites) et la part déjà livrée sans commande (déjà déduite du LP).
  if (!isHistoricalCommande) {
    const { solde, orphelines, repartition } = await calculerSoldeLP(terminalId, produit);
    if (repartition?.demarree && !fourn) throw new Error("Choisissez le fournisseur");
    const besoin = besoinLPCommande(activeRows, orphelines);
    if (besoin > solde + 1e-6) {
      throw new Error(
        `Libre pratique insuffisant : commande ${besoin} m³ mais LP disponible = ` +
          `${Math.round(solde * 1000) / 1000} m³. Ajoutez d'abord le libre pratique.`,
      );
    }
    if (repartition?.demarree && fourn) {
      const soldeF = repartition.parFournisseur[fourn] ?? 0;
      if (besoin > soldeF + 1e-6) {
        throw new Error(
          `Libre pratique ${fourn} insuffisant : commande ${besoin} m³ mais LP ${fourn} = ` +
            `${Math.round(soldeF * 1000) / 1000} m³.`,
        );
      }
    }
  }

  const createdIds = [];
  const submittedBy = await getSubmittedBy();
  const commandeDate = date
    ? Timestamp.fromDate(new Date(date))
    : serverTimestamp();

  await runTransaction(db, async (tx) => {
    // ── Lecture LP ──
    const lpSnap = await tx.get(lpRef);
    if (!lpSnap.exists())
      throw new Error(`LP ${terminalId}/${produit} introuvable`);
    // Le contrôle LP suffisant est fait avant la transaction (cf. plus haut).

    // ── Lecture stations ──
    const stationIds = [...new Set(activeRows.map((r) => r.stationId))];
    const stationDataMap = {};
    const erreurs = [];
    for (const stationId of stationIds) {
      const staSnap = await tx.get(doc(db, "stations", stationId));
      if (!staSnap.exists())
        throw new Error(`Station ${stationId} introuvable`);
      const sta = staSnap.data();
      stationDataMap[stationId] = sta;
      const stockActuel = sta.stock?.[produit] ?? 0;
      const capaciteMax = sta.capacite?.[produit] ?? 0;
      const espaceLibre = capaciteMax - stockActuel;
      const stationTotal = activeRows
        .filter((r) => r.stationId === stationId)
        .reduce((a, r) => a + r.qty, 0);
      if (!isHistoricalCommande && stationTotal > espaceLibre) {
        erreurs.push(
          `${sta.label}: commandé ${stationTotal} m³ mais espace dispo = ${espaceLibre} m³ ` +
            `(capacité ${capaciteMax} m³ − stock ${stockActuel} m³)`,
        );
      }
    }
    if (erreurs.length > 0) {
      throw new Error("Capacité dépassée :\n" + erreurs.join("\n"));
    }

    // ── Vérification doublons d'ID ──
    for (const row of activeRows) {
      const cmdId = `${row.stationId.toUpperCase()}_${numProgramme.trim()}_${row.bl.trim()}`;
      const existing = await tx.get(doc(db, "commandes", cmdId));
      if (existing.exists()) {
        throw new Error(`Commande ${cmdId} existe déjà`);
      }
    }

    // ── Écritures ──
    const stationsResume = [];
    for (const row of activeRows) {
      const cmdId = `${row.stationId.toUpperCase()}_${numProgramme.trim()}_${row.bl.trim()}`;
      const cmdRef = doc(db, "commandes", cmdId);
      tx.set(cmdRef, {
        produit,
        terminal: terminalId,
        station: row.stationId.toUpperCase(),
        numProg: numProgramme.trim(),
        numBL: row.bl.trim(),
        fournisseur: fourn,
        qtyCom: row.qty,
        qtyLiv: 0,
        statut: "en_attente",
        isHistorical: isHistoricalCommande,
        date: commandeDate,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      createdIds.push(cmdId);
      stationsResume.push({
        station: row.stationId,
        qty: row.qty,
        bl: row.bl.trim(),
      });
    }

    // ── Une seule entrée journal consolidée pour le programme (pour Slack) ──
    const totalCommande = activeRows.reduce((a, r) => a + r.qty, 0);
    const journalRef = doc(db, "journal", journalId(numProgramme.trim()));
    tx.set(journalRef, {
      type: "Commande",
      terminal: terminalId,
      produit,
      quantite: totalCommande,
      reference: numProgramme.trim(),
      station: null,
      camion: null,
      stations: stationsResume,
      commandeIds: createdIds,
      fournisseur: fourn,
      submittedBy,
      note: `Programme ${numProgramme.trim()} — ${activeRows.length} commande(s)${fourn ? ` — ${fourn}` : ""}`,
      createdAt: serverTimestamp(),
      date: commandeDate,
    });
  });

  return createdIds;
}

// ════════════════════════════════════════════════════════════
// ÉCRITURE — MODIFIER UNE COMMANDE EN ATTENTE
// ════════════════════════════════════════════════════════════

/**
 * Modifie la station et/ou la quantité d'une commande en_attente.
 * Si la station change, l'ancien document est supprimé et un nouveau créé
 * (l'ID encode la station).
 *
 * @param {{ id, station, numProg, numBL }} commande - commande existante
 * @param {{ newStation: string, newQtyCom: number, newFournisseur?: string }} params
 * @returns {string} ID de la commande résultante
 */
export async function modifierCommande(
  commande,
  { newStation, newQtyCom, newFournisseur },
) {
  const oldRef = doc(db, "commandes", commande.id);
  const stationChanged = newStation.toUpperCase() !== commande.station;
  // undefined = fournisseur inchangé
  const fournisseurApres =
    newFournisseur === undefined
      ? commande.fournisseur || null
      : normFournisseur(newFournisseur) || null;

  // Une hausse de quantité doit tenir dans le libre pratique disponible
  // (même règle qu'à la création ; baisse et changement de station libres,
  // commandes historiques exemptées).
  const hausse = Number(newQtyCom) - Number(commande.qtyCom || 0);
  if (hausse > 0 && !commande.isHistorical) {
    const { solde, repartition } = await calculerSoldeLP(commande.terminal, commande.produit);
    if (hausse > solde + 1e-6) {
      throw new Error(
        `Libre pratique insuffisant : +${hausse} m³ demandés mais LP disponible = ` +
          `${Math.round(solde * 1000) / 1000} m³. Ajoutez d'abord le libre pratique.`,
      );
    }
    if (repartition?.demarree && fournisseurApres) {
      const soldeF = repartition.parFournisseur[fournisseurApres] ?? 0;
      if (hausse > soldeF + 1e-6) {
        throw new Error(
          `Libre pratique ${fournisseurApres} insuffisant : +${hausse} m³ demandés mais ` +
            `LP ${fournisseurApres} = ${Math.round(soldeF * 1000) / 1000} m³.`,
        );
      }
    }
  }

  const submittedBy = await getSubmittedBy();

  let resultId = commande.id;

  if (stationChanged) {
    const newId = `${newStation.toUpperCase()}_${commande.numProg}_${commande.numBL}`;
    const newRef = doc(db, "commandes", newId);
    await runTransaction(db, async (tx) => {
      const oldSnap = await tx.get(oldRef);
      if (!oldSnap.exists()) throw new Error("Commande introuvable");
      const already = await tx.get(newRef);
      if (already.exists()) throw new Error(`Commande ${newId} existe déjà`);
      tx.set(newRef, {
        ...oldSnap.data(),
        station: newStation.toUpperCase(),
        qtyCom: newQtyCom,
        fournisseur: fournisseurApres,
        updatedAt: serverTimestamp(),
      });
      tx.delete(oldRef);
    });
    resultId = newId;
  } else {
    await updateDoc(oldRef, {
      qtyCom: newQtyCom,
      fournisseur: fournisseurApres,
      updatedAt: serverTimestamp(),
    });
  }

  // Journal entry → triggers gestockToSheet → Slack notification
  await setDoc(doc(db, "journal", journalId(`MOD_${commande.id}`)), {
    type: "ModificationCommande",
    terminal: commande.terminal,
    produit: commande.produit,
    reference: commande.numProg,
    quantite: newQtyCom,
    commandeId: commande.id,
    commandeIdApres: resultId,
    avant: {
      station: commande.station,
      qtyCom: commande.qtyCom,
      fournisseur: commande.fournisseur || null,
    },
    apres: {
      station: newStation.toUpperCase(),
      qtyCom: newQtyCom,
      fournisseur: fournisseurApres,
    },
    submittedBy,
    note: `Modification commande ${commande.id}`,
    createdAt: serverTimestamp(),
    date: serverTimestamp(),
  });

  return resultId;
}

// ════════════════════════════════════════════════════════════
// ÉCRITURE — SUPPRIMER UNE COMMANDE NON LIVRÉE (admin)
// ════════════════════════════════════════════════════════════

/**
 * Supprime une commande qui n'a reçu aucune livraison. Le LP n'étant déduit
 * que par les livraisons, rien n'est à restituer.
 *
 * @param {{ id, terminal, produit, station, numProg, numBL, qtyCom, fournisseur }} commande
 */
export async function supprimerCommande(commande) {
  const cmdRef = doc(db, "commandes", commande.id);
  // Les transactions ne lisent pas de collection → contrôle avant
  const livSnap = await getDocs(collection(db, "commandes", commande.id, "livraisons"));
  if (!livSnap.empty) {
    throw new Error("Commande déjà livrée (même partiellement) — suppression impossible");
  }
  const submittedBy = await getSubmittedBy();

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(cmdRef);
    if (!snap.exists()) throw new Error("Commande introuvable");
    if (Number(snap.data().qtyLiv || 0) > 0) {
      throw new Error("Commande déjà livrée (même partiellement) — suppression impossible");
    }
    tx.delete(cmdRef);
    // Journal entry → triggers gestockToSheet → Slack notification
    tx.set(doc(db, "journal", journalId(`SUPPR_${commande.id}`)), {
      type: "SuppressionCommande",
      terminal: commande.terminal,
      produit: commande.produit,
      reference: commande.numProg,
      numBL: commande.numBL,
      quantite: commande.qtyCom,
      commandeId: commande.id,
      station: commande.station,
      fournisseur: commande.fournisseur || null,
      submittedBy,
      note: `Suppression commande ${commande.id}`,
      createdAt: serverTimestamp(),
      date: serverTimestamp(),
    });
  });
}

// ════════════════════════════════════════════════════════════
// ÉCRITURE — SUPPRIMER UNE LIVRAISON SANS COMMANDE
// ════════════════════════════════════════════════════════════

/**
 * Supprime une livraison orpheline ("Commande introuvable"). Le LP étant
 * calculé comme LP brut − livraisons − orphelines, la quantité déduite est
 * restituée au libre pratique.
 *
 * L'entrée journal (même ID) est conservée — approvToJournal ne recrée pas
 * une livraison dont le journal existe — mais sortie du rattachement
 * automatique (commandeCreatedBackfill ne cherche que commandeMatched == false).
 *
 * @param {{ terminalId: string, orphelineId: string }} params
 */
export async function supprimerLivraisonOrpheline({ terminalId, orphelineId }) {
  const orphRef = doc(
    db,
    "terminaux",
    terminalId,
    "livraisons_orphelines",
    orphelineId,
  );
  const journalRef = doc(db, "journal", orphelineId);
  const submittedBy = await getSubmittedBy();

  await runTransaction(db, async (tx) => {
    const orphSnap = await tx.get(orphRef);
    if (!orphSnap.exists()) throw new Error("Livraison introuvable");
    const journalSnap = await tx.get(journalRef);

    if (journalSnap.exists()) {
      const j = journalSnap.data();
      tx.update(journalRef, {
        commandeMatched: null,
        livraisonSupprimee: true,
        supprimeePar: submittedBy,
        supprimeeLe: serverTimestamp(),
        note: `${j.note || "Livraison"} — supprimée (LP restitué)`,
      });
    }
    tx.delete(orphRef);
  });
}

// ════════════════════════════════════════════════════════════
// ÉCRITURE — RATTACHER UNE LIVRAISON SANS COMMANDE À UNE COMMANDE EXISTANTE
// ════════════════════════════════════════════════════════════

/**
 * Rattache une livraison orpheline (ex. BL mal saisi par la station) à une
 * commande existante du même terminal/produit/station, si le reste à livrer
 * de la commande couvre la quantité. Comme commandeCreatedBackfill : crée la
 * livraison sous la commande (datée de la livraison réelle), met à jour
 * qtyLiv/statut, supprime l'orpheline (le LP total ne change pas) et marque
 * l'entrée journal comme rattachée.
 *
 * @param {{ terminalId: string, orphelineId: string, commandeId: string }} params
 */
/**
 * Commandes existantes du terminal/produit portant l'un de ces N° BL.
 * Requête sur numBL seul (pas d'index composite), filtre terminal/produit
 * côté client ; `in` est limité à 30 valeurs → découpage.
 *
 * @returns {Array<{id:string, numBL:string}>}
 */
export async function blsDejaUtilises(terminalId, produit, bls) {
  const uniques = [...new Set(bls.map((b) => String(b).trim()).filter(Boolean))];
  const trouves = [];
  for (let i = 0; i < uniques.length; i += 30) {
    const snap = await getDocs(
      query(collection(db, "commandes"), where("numBL", "in", uniques.slice(i, i + 30))),
    );
    snap.forEach((d) => {
      const c = d.data();
      if (c.terminal === terminalId && c.produit === produit) {
        trouves.push({ id: d.id, numBL: c.numBL });
      }
    });
  }
  return trouves;
}

/** true si la commande existe déjà (ID = STATION_PROG_BL). */
export async function commandeExiste(commandeId) {
  return (await getDoc(doc(db, "commandes", commandeId))).exists();
}

export async function rattacherLivraisonOrpheline({ terminalId, orphelineId, commandeId }) {
  const orphRef = doc(db, "terminaux", terminalId, "livraisons_orphelines", orphelineId);
  const cmdRef = doc(db, "commandes", commandeId);
  const journalRef = doc(db, "journal", orphelineId);
  const livRef = doc(db, "commandes", commandeId, "livraisons", `liv_rattache_${orphelineId}`);

  // Livré actuel = somme des livraisons (les transactions ne lisent pas de collection)
  const livSnap = await getDocs(collection(db, "commandes", commandeId, "livraisons"));
  const dejaLivre = livSnap.docs.reduce((a, d) => a + (d.data().volumeM3 || 0), 0);
  const submittedBy = await getSubmittedBy();

  await runTransaction(db, async (tx) => {
    const orphSnap = await tx.get(orphRef);
    if (!orphSnap.exists()) throw new Error("Livraison introuvable");
    const cmdSnap = await tx.get(cmdRef);
    if (!cmdSnap.exists()) throw new Error(`Commande ${commandeId} introuvable`);
    const journalSnap = await tx.get(journalRef);

    const o = orphSnap.data();
    const cmd = cmdSnap.data();
    if (cmd.terminal !== terminalId || cmd.produit !== o.produit) {
      throw new Error("La commande n'est pas du même terminal / produit");
    }
    if (String(cmd.station || "").toLowerCase() !== String(o.station || "").toLowerCase()) {
      throw new Error(`La commande est pour ${cmd.station}, la livraison pour ${String(o.station || "").toUpperCase()}`);
    }
    const qte = o.volumeM3 || 0;
    const reste = (cmd.qtyCom || 0) - dejaLivre;
    if (qte > reste + 1e-6) {
      throw new Error(
        `Reste à livrer insuffisant sur ${commandeId} : ${fmtDec(reste, 3)} m³ pour une livraison de ${fmtDec(qte, 3)} m³`,
      );
    }

    const newQtyLiv = dejaLivre + qte;
    tx.set(livRef, {
      volumeM3: qte,
      camion: "—",
      date: o.createdAt || serverTimestamp(),
      createdAt: serverTimestamp(),
      rattacheDe: { orphelineId, numBL: o.numBL || null },
    });
    tx.update(cmdRef, {
      qtyLiv: newQtyLiv,
      statut: newQtyLiv >= (cmd.qtyCom || 0) - 1e-6 ? "cloture" : "partiel",
      updatedAt: serverTimestamp(),
    });
    if (journalSnap.exists()) {
      const j = journalSnap.data();
      tx.update(journalRef, {
        commandeMatched: true,
        commandeId,
        reference: cmd.numBL || j.reference,
        referenceOrigine: j.reference || o.numBL || null,
        rattachePar: submittedBy,
        rattacheLe: serverTimestamp(),
        note: `${j.note || "Livraison"} — rattachée à ${commandeId} (BL saisi ${o.numBL || "—"})`,
      });
    }
    tx.delete(orphRef);
  });
}

// ════════════════════════════════════════════════════════════
// ÉCRITURE — DÉCLARER UNE LIVRAISON PARTIELLE
// ════════════════════════════════════════════════════════════

/**
 * Déclare une livraison partielle ou totale pour une commande :
 *   1. Validation : qtyCom - qtyLiv ≥ quantite, capacité station
 *   2. Crée commandes/{id}/livraisons/{livId}
 *   3. Met à jour qtyLiv et statut sur la commande
 *   4. Augmente le stock station
 *   5. Écrit l'entrée journal
 *
 * Note : LP n'est PAS décrémenté ici. watchTerminalStocks soustrait
 * la somme des livraisons du LP doc pour afficher le LP réel.
 *
 * @param {Object} params
 * @param {string} params.commandeId
 * @param {number} params.quantite
 * @param {string} params.camion
 */
export async function declarerLivraison({ commandeId, quantite, camion }) {
  const cmdRef = doc(db, "commandes", commandeId);
  const livraisonsRef = collection(db, "commandes", commandeId, "livraisons");

  // Pré-fetch livraisons existantes pour calculer qtyLiv hors transaction
  // (les transactions ne peuvent pas lire de collections, seulement des docs)
  const existingLivSnap = await getDocs(livraisonsRef);
  const dejaLivre = existingLivSnap.docs.reduce(
    (s, d) => s + (d.data().volumeM3 || 0),
    0,
  );

  // ID livraison séquentiel : liv_YYYYMMDD_n
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const livId = `liv_${dateStr}_${existingLivSnap.size + 1}`;
  const livRef = doc(db, "commandes", commandeId, "livraisons", livId);
  const submittedBy = await getSubmittedBy();

  await runTransaction(db, async (tx) => {
    const cmdSnap = await tx.get(cmdRef);
    if (!cmdSnap.exists()) throw new Error("Commande introuvable");
    const cmd = cmdSnap.data();

    if (quantite <= 0) throw new Error("Quantité invalide");

    const reste = (cmd.qtyCom || 0) - dejaLivre;
    if (quantite > reste) throw new Error(`Max à livrer: ${reste} m³`);

    // Lecture station (label en lowercase pour correspondre au doc)
    const stationId = (cmd.station || "").toLowerCase();
    const staRef = doc(db, "stations", stationId);
    const staSnap = await tx.get(staRef);
    if (!staSnap.exists()) throw new Error(`Station ${stationId} introuvable`);
    const sta = staSnap.data();

    const stockSta = sta.stock?.[cmd.produit] ?? 0;
    const capacite = sta.capacite?.[cmd.produit] ?? 0;
    if (stockSta + quantite > capacite) {
      throw new Error(
        `Capacité station dépassée: ${stockSta} m³ + ${quantite} m³ > ${capacite} m³`,
      );
    }

    const newQtyLiv = dejaLivre + quantite;
    const statut = newQtyLiv >= cmd.qtyCom ? "cloture" : "partiel";

    // 1. Livraison
    tx.set(livRef, {
      volumeM3: quantite,
      camion: camion || "—",
      date: serverTimestamp(),
      createdAt: serverTimestamp(),
    });

    // 2. Commande
    tx.update(cmdRef, {
      qtyLiv: newQtyLiv,
      statut,
      updatedAt: serverTimestamp(),
    });

    // 3. Stock station
    tx.update(staRef, {
      [`stock.${cmd.produit}`]: stockSta + quantite,
      updatedAt: serverTimestamp(),
    });

    // 4. Journal
    const journalRef = doc(
      db,
      "journal",
      journalId(`${cmd.numBL || commandeId}_${livId}`),
    );
    tx.set(journalRef, {
      type: "Livraison",
      terminal: cmd.terminal,
      produit: cmd.produit,
      quantite,
      reference: cmd.numBL || commandeId,
      commandeId,
      station: stationId,
      stationLabel: sta.label,
      camion: camion || "—",
      submittedBy,
      note: `Livraison ${statut === "cloture" ? "complète" : "partielle"} → ${sta.label}`,
      createdAt: serverTimestamp(),
    });
  });
}

// ════════════════════════════════════════════════════════════
// ÉCRITURE — ENREGISTRER UNE VENTE
// ════════════════════════════════════════════════════════════

export async function enregistrerVente(stationId, produit, quantite) {
  const staRef = doc(db, "stations", stationId);
  const submittedBy = await getSubmittedBy();

  return await runTransaction(db, async (tx) => {
    const staSnap = await tx.get(staRef);
    if (!staSnap.exists()) throw new Error("Station introuvable");
    const sta = staSnap.data();

    const stockActuel = sta.stock?.[produit] ?? 0;
    if (quantite <= 0) throw new Error("Quantité invalide");
    if (quantite > stockActuel)
      throw new Error(`Stock insuffisant: ${stockActuel} m³`);

    const nouveauStock = stockActuel - quantite;

    tx.update(staRef, {
      [`stock.${produit}`]: nouveauStock,
      updatedAt: serverTimestamp(),
    });

    const ref = `VTE-${Date.now().toString().slice(-6)}`;
    const journalRef = doc(db, "journal", journalId(ref));
    tx.set(journalRef, {
      type: "Vente",
      terminal: null,
      produit,
      quantite,
      reference: ref,
      station: stationId,
      stationLabel: sta.label,
      camion: null,
      submittedBy,
      note: `Vente ${produit === "essence" ? "Super" : "Go"} — ${sta.label}`,
      createdAt: serverTimestamp(),
    });

    return nouveauStock;
  });
}

// ════════════════════════════════════════════════════════════
// UTILITAIRES
// ════════════════════════════════════════════════════════════

export function calcEspaceDisponible(station, produit) {
  const capacite = station.capacite?.[produit] ?? 0;
  const stockActuel = station.stock?.[produit] ?? 0;
  return {
    capacite,
    stockActuel,
    espaceLibre: Math.max(0, capacite - stockActuel),
  };
}
